import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useRole } from '@/hooks/use-role';

interface AdminModeContextType {
  isAdminMode: boolean;
  toggleAdminMode: () => void;
  isActualAdmin: boolean;
}

const AdminModeContext = createContext<AdminModeContextType | undefined>(undefined);

export const useAdminMode = () => {
  const context = useContext(AdminModeContext);
  if (!context) {
    throw new Error('useAdminMode must be used within AdminModeProvider');
  }
  return context;
};

interface AdminModeProviderProps {
  children: ReactNode;
}

export const AdminModeProvider = ({ children }: AdminModeProviderProps) => {
  const { isAdmin } = useRole();
  const [isAdminMode, setIsAdminMode] = useState(() => {
    // Load admin mode preference from localStorage
    const saved = localStorage.getItem('adminModeEnabled');
    return saved === 'true';
  });

  // Reset admin mode when user logs out or is not an admin
  useEffect(() => {
    if (!isAdmin) {
      setIsAdminMode(false);
      localStorage.setItem('adminModeEnabled', 'false');
    }
  }, [isAdmin]);

  const toggleAdminMode = () => {
    if (!isAdmin) {
      // Only admins can toggle admin mode
      return;
    }
    const newMode = !isAdminMode;
    setIsAdminMode(newMode);
    localStorage.setItem('adminModeEnabled', String(newMode));
  };

  return (
    <AdminModeContext.Provider
      value={{
        isAdminMode: isAdmin && isAdminMode, // Only true if user is admin AND mode is enabled
        toggleAdminMode,
        isActualAdmin: isAdmin,
      }}
    >
      {children}
    </AdminModeContext.Provider>
  );
};

